// Sana v35 (15 Sep 2026) — magnifier that never shows more pixels than the
// source file has. Dawn's original loaded `image.src` (a width=1946 CDN URL)
// and always zoomed 2x, which on a Retina display stretched a 1500px file to
// ~3400 device pixels — the "blurry zoom" complaint. Now: the overlay loads
// the largest rendition the CDN can give (width=3000; the CDN caps at the
// file's real size), and the zoom ratio is derived from the file's real
// pixel width vs the pixels already on screen, so magnification stops at
// 1:1 of the source (clamped between 1.2x and 3x). Hero images upscaled to
// 3000px therefore zoom roughly twice as deep as untouched 1500px ones.
function fullSizeUrl(src) {
  try {
    const u = new URL(src, location.href);
    u.searchParams.set('width', '3000');
    return u.toString();
  } catch (e) {
    return src;
  }
}

function createOverlay(image, onReady) {
  const overlayImage = document.createElement('img');
  overlayImage.setAttribute('src', fullSizeUrl(image.currentSrc || image.src));
  overlay = document.createElement('div');
  prepareOverlay(overlay, overlayImage);

  image.style.opacity = '50%';
  toggleLoadingSpinner(image);

  overlayImage.onload = () => {
    toggleLoadingSpinner(image);
    image.parentElement.insertBefore(overlay, image);
    image.style.opacity = '100%';
    if (onReady) onReady(overlayImage.naturalWidth || 0);
  };
  overlayImage.onerror = () => {
    toggleLoadingSpinner(image);
    image.style.opacity = '100%';
  };

  return overlay;
}

function prepareOverlay(container, image) {
  container.setAttribute('class', 'image-magnify-full-size');
  container.setAttribute('aria-hidden', 'true');
  container.style.backgroundImage = `url('${image.src}')`;
  container.style.backgroundColor = 'var(--gradient-background)';
}

function toggleLoadingSpinner(image) {
  const loadingSpinner = image.parentElement.parentElement.querySelector(`.loading__spinner`);
  if (loadingSpinner) loadingSpinner.classList.toggle('hidden');
}

function moveWithHover(image, event, zoomRatio) {
  // calculate mouse position
  const ratio = image.height / image.width;
  const container = event.target.getBoundingClientRect();
  const xPosition = event.clientX - container.left;
  const yPosition = event.clientY - container.top;
  const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
  const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

  // determine what to show in the frame
  overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
  overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
}

// Zoom no deeper than the file allows: sourcePx / (displayed CSS px * DPR).
// v37: RCJ's Cloud Zoom shows the full file at 1:1 CSS pixels (a 1500px
// file over a 400px display = 3.75x) and does not divide by the screen's
// device-pixel ratio. Match that: ratio = file px / displayed CSS px,
// clamped 1.5x–4x. Dividing by DPR (v35) made the magnifier almost a no-op
// on Retina screens, which read as "still blurry".
function safeZoomRatio(image, sourceWidth, requested) {
  if (!sourceWidth || !image.width) return requested;
  const honest = sourceWidth / image.width;
  return Math.max(1.5, Math.min(4, Math.min(requested, honest)));
}

function magnify(image, zoomRatio) {
  let ratio = zoomRatio;
  const overlay = createOverlay(image, (sourceWidth) => {
    ratio = safeZoomRatio(image, sourceWidth, 4);
  });
  overlay.onclick = () => overlay.remove();
  overlay.onmousemove = (event) => moveWithHover(image, event, ratio);
  overlay.onmouseleave = () => overlay.remove();
}

function enableZoomOnHover(zoomRatio) {
  const images = document.querySelectorAll('.image-magnify-hover');
  images.forEach((image) => {
    image.onclick = (event) => {
      magnify(image, zoomRatio);
      moveWithHover(image, event, zoomRatio);
    };
  });
}

// This file is loaded synchronously (script_tag) before the gallery markup
// exists, so bind after the DOM is parsed.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => enableZoomOnHover(2));
} else {
  enableZoomOnHover(2);
}
